/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Menu from '@mui/material/Menu';
import Pagination from '@mui/material/Pagination';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDCheckbox from 'components/MDCheckbox';
import MDTypography from 'components/MDTypography';
import MDDatePicker from 'components/MDDatePicker';
import TabPanel from 'components/TabPanel/TabPanel';
import TableHeader from 'components/Table/TableHeader';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import CouponProductList from './CouponProductList';
import CouponBrand from './CouponBrand';

const mod = 'coupons';

const initProductFind = {
  search: '',
  order_by: 'pd.id',
  order_sort: 'desc',
  page: 1,
  perPage: 7,
  pageTotal: 0,
};

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  enable: 'ALL',
};

const initAttr = {
  id: '',
  name: '',
  discount_type: 'fixed',
  discount_value: '',
  max_discount_amount: '',
  min_order_amount: '',
  is_condition: 'N',
  is_min_amount: 'N',
  start_date: Func.dateNow(),
  end_date: Func.dateAdd(30),
  enable: 'Y',
  products: [],
};

const initGenCode = {
  mode: 'single',
  code: '',
  amount: 1,
  quota: 1,
  is_limit: 1,
};

const initCodeFind = {
  search: '',
  order_by: 'cc.id',
  order_sort: 'asc',
  page: 1,
  perPage: 20,
  pageTotal: 0,
};

const initAddAttr = {
  name: '',
  discount_type: 'fixed',
  discount_value: '',
  max_discount_amount: '',
  min_order_amount: '',
  is_condition: 'N',
  is_min_amount: 'N',
  start_date: Func.dateNow(),
  end_date: Func.dateAdd(30),
  enable: 'Y',
};

function CouponDetail({ id: idProp, onBack }) {
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id: idParam } = useParams();
  const id = idProp ?? idParam;

  // ---- List state ----
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [showMenu, setShowMenu] = useState(null);
  const [addAttr, setAddAttr] = useState(initAddAttr);
  const [find, setFind] = useState(initFind);
  const [confirmAdd, setConfirmAdd] = useState(false);
  const [orderList, setOrderList] = useState(initFind.order_sort);
  const [orderByList, setOrderByList] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);

  // ---- Detail state ----
  const [attr, setAttr] = useState(initAttr);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);

  const attrDiscountValues = useRef({ fixed: '', percentage: '' });
  const addAttrDiscountValues = useRef({ fixed: '', percentage: '' });

  // Generate code modal
  const [modalGen, setModalGen] = useState(false);
  const [genCode, setGenCode] = useState(initGenCode);
  const [confirmGen, setConfirmGen] = useState(false);

  // Add product modal
  const [modalProduct, setModalProduct] = useState(false);
  const [productFind, setProductFind] = useState(initProductFind);
  const [productList, setProductList] = useState([]);
  const [productPageTotal, setProductPageTotal] = useState(0);
  const [productPageNo, setProductPageNo] = useState(1);
  const [productLoading, setProductLoading] = useState(false);
  const [productOrder, setProductOrder] = useState(initProductFind.order_sort);
  const [productOrderBy, setProductOrderBy] = useState(initProductFind.order_by);
  const [productShowMenu, setProductShowMenu] = useState(null);
  const txtProductSearch = useRef(null);

  // Product list tab (frontend pagination)
  const [productSearch, setProductSearch] = useState('');
  const [productPage, setProductPage] = useState(1);
  const [productPerPage, setProductPerPage] = useState(10);
  const [productPageShowMenu, setProductPageShowMenu] = useState(null);

  // Generated codes list
  const [codeList, setCodeList] = useState([]);
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeFind, setCodeFind] = useState(initCodeFind);
  const [codePageTotal, setCodePageTotal] = useState(0);
  const [codePageNo, setCodePageNo] = useState(1);
  const [codeOrder, setCodeOrder] = useState(initCodeFind.order_sort);
  const [codeOrderBy, setCodeOrderBy] = useState(initCodeFind.order_by);
  const [codeShowMenu, setCodeShowMenu] = useState(null);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const renderLoading = () => (
    <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
      <CircularProgress color='inherit' />
    </Backdrop>
  );

  // ---- List header ----
  const initHeader = [
    { id: 'tb.id', label: t('attr.id'), align: 'center', width: 60 },
    { id: 'tb.name', label: 'ชื่อคูปอง', align: 'left' },
    { id: 'tb.discount_type', label: 'ประเภทส่วนลด', align: 'center', width: 120 },
    { id: 'tb.discount_value', label: 'มูลค่าส่วนลด', align: 'right', width: 120 },
    { id: 'tb.start_date', label: 'ช่วงเวลา', align: 'center', width: 180 },
    { id: 'tb.enable', label: t('attr.enable'), align: 'center', width: 60, sort: false },
  ];

  const openShow = (e) => setShowMenu(e.currentTarget);
  const closeShow = () => setShowMenu(null);

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      enable: data.enable,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data ?? []);
        setPageTotal(result.total_pages ?? 1);
        setPageNo(result.page ?? 1);
      }
      setLoading(false);
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(addAttr.name, 1)) {
      showNotify('กรุณากรอกชื่อคูปอง', 'error');
      return;
    }
    if (!Func.verifyLength(addAttr.discount_value.toString(), 1)) {
      showNotify('กรุณากรอกมูลค่าส่วนลด', 'error');
      return;
    }
    setConfirmAdd(true);
  };

  const addCoupon = () => {
    setLoading(true);
    setModalAdd(false);
    FetchData('POST', `${mod}`, app.token, {
      name: addAttr.name,
      discount_type: addAttr.discount_type,
      discount_value: parseFloat(addAttr.discount_value) || 0,
      max_discount_amount:
        addAttr.max_discount_amount !== '' ? parseFloat(addAttr.max_discount_amount) : null,
      min_order_amount:
        addAttr.is_min_amount === 'Y' ? parseFloat(addAttr.min_order_amount) || 0 : null,
      is_condition: addAttr.is_condition,
      is_min_amount: addAttr.is_min_amount,
      start_date: addAttr.start_date,
      end_date: addAttr.end_date,
      enable: addAttr.enable,
    }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        view(find);
        showNotify(t('alert.addcomplete'), 'success');
        if (result.id) {
          window.location.href = `/coupon_list/${result.id}`;
        }
      } else {
        setModalAdd(true);
        showNotify(t('alert.saveerror'), 'error');
      }
    });
  };

  const handleSort = (e, property) => {
    const isAsc = orderByList === property && orderList === 'asc';
    setOrderList(isAsc ? 'desc' : 'asc');
    setOrderByList(property);
    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const discountTypeDisplay = (type) => {
    if (type === 'fixed')
      return <Chip label='คงที่ (฿)' size='small' color='primary' variant='outlined' />;
    if (type === 'percentage')
      return <Chip label='เปอร์เซ็นต์ (%)' size='small' color='info' variant='outlined' />;
    return type;
  };

  const enableDisplay = (inp) => {
    if (inp === 'Y') return <Icon color='success'>check_circle</Icon>;
    return <Icon color='error'>not_interested</Icon>;
  };

  const btnOpen = (item) => (
    <NavLink to={`/coupon_list/${item.id}`}>
      <MDButton
        variant='text'
        color='info'
        size='small'
        sx={{ fontSize: '0.85rem', fontWeight: 'bold', minWidth: 0 }}
      >
        #{item.id}
      </MDButton>
    </NavLink>
  );

  const renderShowMenu = (
    <Menu
      anchorEl={showMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(showMenu)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t('element.show_record', { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t('element.show_record', { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t('element.show_record', { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const renderSearch = () => (
    <Card sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <MDBox
            noValidate
            component='form'
            role='form'
            onSubmit={(e) => {
              e.preventDefault();
              view({ ...find, page: 1 });
            }}
          >
            <MDInput
              name='searchform'
              fullWidth
              value={find.search}
              type='search'
              onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                ),
                autoComplete: 'off',
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <MDInput
            fullWidth
            value={find.enable}
            defaultValue='ALL'
            select
            onChange={(e) => view({ ...find, enable: e.target.value, page: 1 })}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <span>เปิดใช้ :</span>
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value='ALL'>ทั้งหมด</MenuItem>
            <MenuItem value='Y'>เปิด</MenuItem>
            <MenuItem value='N'>ปิด</MenuItem>
          </MDInput>
        </Grid>
      </Grid>
    </Card>
  );

  const renderFormAdd = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <MDInput
          label='ชื่อคูปอง *'
          fullWidth
          variant='standard'
          value={addAttr.name}
          onChange={(e) => setAddAttr({ ...addAttr, name: e.target.value })}
          inputProps={{ autoComplete: 'off' }}
        />
      </Grid>
      <Grid item xs={12}>
        <MDInput
          fullWidth
          label='ประเภทส่วนลด *'
          value={addAttr.discount_type}
          select
          variant='standard'
          onChange={(e) => {
            const newType = e.target.value;
            addAttrDiscountValues.current[addAttr.discount_type] = addAttr.discount_value;
            const restored = addAttrDiscountValues.current[newType];

            setAddAttr({
              ...addAttr,
              discount_type: newType,
              discount_value: restored !== '' && restored !== undefined ? restored : 0,
            });
          }}
        >
          <MenuItem value='fixed'>คงที่ (฿)</MenuItem>
          <MenuItem value='percentage'>เปอร์เซ็นต์ (%)</MenuItem>
        </MDInput>
      </Grid>
      <Grid item xs={12}>
        <MDInput
          label={`มูลค่าส่วนลด * ${addAttr.discount_type === 'percentage' ? '(%)' : '(฿)'}`}
          fullWidth
          variant='standard'
          value={addAttr.discount_value}
          onChange={(e) => {
            addAttrDiscountValues.current[addAttr.discount_type] = e.target.value;
            setAddAttr({ ...addAttr, discount_value: e.target.value });
          }}
          inputProps={{ autoComplete: 'off', type: 'number', min: 0 }}
        />
      </Grid>
      {addAttr.discount_type === 'percentage' && (
        <Grid item xs={12}>
          <MDInput
            label='ส่วนลดสูงสุด (฿)'
            fullWidth
            variant='standard'
            value={addAttr.max_discount_amount}
            onChange={(e) => setAddAttr({ ...addAttr, max_discount_amount: e.target.value })}
            inputProps={{ autoComplete: 'off', type: 'number', min: 0 }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <MDCheckbox
          label='มีเงื่อนไขยอดขั้นต่ำ'
          checked={addAttr.is_min_amount === 'Y'}
          onChange={(e) => setAddAttr({ ...addAttr, is_min_amount: e.target.checked ? 'Y' : 'N' })}
        />
      </Grid>
      {addAttr.is_min_amount === 'Y' && (
        <Grid item xs={12}>
          <MDInput
            label='ยอดสั่งซื้อขั้นต่ำ (฿) *'
            fullWidth
            variant='standard'
            value={addAttr.min_order_amount}
            onChange={(e) => setAddAttr({ ...addAttr, min_order_amount: e.target.value })}
            inputProps={{ autoComplete: 'off', type: 'number', min: 0 }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <MDCheckbox
          label='ใช้ได้เฉพาะสินค้าที่กำหนด'
          checked={addAttr.is_condition === 'Y'}
          onChange={(e) => setAddAttr({ ...addAttr, is_condition: e.target.checked ? 'Y' : 'N' })}
        />
      </Grid>
      <Grid item xs={6}>
        <MDDatePicker
          onChange={([date]) => setAddAttr({ ...addAttr, start_date: Func.dateFormat(date) })}
          value={new Date(addAttr.start_date)}
          input={{ label: 'วันที่เริ่มต้น *', variant: 'standard', fullWidth: true }}
        />
      </Grid>
      <Grid item xs={6}>
        <MDDatePicker
          onChange={([date]) => setAddAttr({ ...addAttr, end_date: Func.dateFormat(date) })}
          value={new Date(addAttr.end_date)}
          input={{ label: 'วันที่สิ้นสุด *', variant: 'standard', fullWidth: true }}
        />
      </Grid>
      <Grid item xs={12}>
        <MDCheckbox
          label={t('attr.enable')}
          checked={addAttr.enable === 'Y'}
          onChange={(e) => setAddAttr({ ...addAttr, enable: e.target.checked ? 'Y' : 'N' })}
        />
      </Grid>
    </Grid>
  );

  const renderAdd = () => (
    <Dialog
      open={modalAdd}
      maxWidth='sm'
      keepMounted
      aria-describedby='dialog-add-coupon'
      fullWidth
    >
      <DialogTitle>
        สร้างคูปองใหม่
        <IconButton
          aria-label='close'
          onClick={() => setModalAdd(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <MDBox p={1}>{renderFormAdd()}</MDBox>
      </DialogContent>
      <DialogActions>
        {confirmAdd ? (
          <Grid container>
            <Grid item xs={6}>
              <MDButton variant='text' color='error' onClick={() => setConfirmAdd(false)}>
                <Icon>cancel</Icon>&nbsp;{t('element.cancel')}
              </MDButton>
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              <MDButton variant='outlined' color='success' onClick={addCoupon}>
                <Icon>save</Icon>&nbsp;{t('element.saveconfirm')}
              </MDButton>
            </Grid>
          </Grid>
        ) : (
          <MDButton variant='text' color='info' onClick={addConfirm}>
            <Icon>save</Icon>&nbsp;{t('element.save')}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );

  const renderList = () => (
    <DashboardLayout>
      <Navbar route={[t('menu.pos'), t('menu.coupon_list')]} />
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              onClick={() => {
                setAddAttr(initAddAttr);
                addAttrDiscountValues.current = { fixed: '', percentage: '' };
                setConfirmAdd(false);
                setModalAdd(true);
              }}
            >
              <Icon>post_add</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='coupon-table'>
                <TableHeader
                  order={orderList}
                  orderBy={orderByList}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{btnOpen(row)}</TableCell>
                      <TableCell align='left'>
                        <MDTypography variant='caption' fontWeight='medium'>
                          {row.name}
                        </MDTypography>
                      </TableCell>
                      <TableCell align='center'>{discountTypeDisplay(row.discount_type)}</TableCell>
                      <TableCell align='right' className='nowrap'>
                        <MDTypography variant='caption' color='info' fontWeight='bold'>
                          {Func.numberDisplay(row.discount_value)}
                          {row.discount_type === 'percentage' ? '%' : ' ฿'}
                        </MDTypography>
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        <MDTypography variant='caption'>
                          {Func.dateDisplay(row.start_date)} – {Func.dateDisplay(row.end_date)}
                        </MDTypography>
                      </TableCell>
                      <TableCell align='center'>{enableDisplay(row.enable)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton
                  variant={showMenu ? 'contained' : 'outlined'}
                  color='dark'
                  onClick={openShow}
                >
                  {t('element.show_record', { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShowMenu}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      {renderAdd()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );

  const open = () => {
    setLoading(true);
    setAttr(initAttr);
    setProductSearch('');
    setProductPage(1);

    FetchData('GET', `/${mod}/${id}`, app.token, { lang: app.lang }).then((result) => {
      if (result.status === 'ok') {
        const data = { ...initAttr, ...result.data, products: result.data.products ?? [] };
        setAttr(data);
        attrDiscountValues.current = { fixed: '', percentage: '' };
        attrDiscountValues.current[data.discount_type] = data.discount_value ?? '';
        setCodeList(result.data.codes ?? []);
      }
      setLoading(false);
    });
  };

  const viewCodes = (data) => {
    setCodeFind(data);
    setCodeLoading(true);
    setCodeList([]);
    setCodeShowMenu(null);
    FetchData('POST', `/${mod}/codes`, app.token, {
      coupon_id: parseInt(id, 10),
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setCodeList(result.data ?? []);
        setCodePageTotal(result.total_pages ?? 1);
        setCodePageNo(result.page ?? 1);
      }
      setCodeLoading(false);
    });
  };

  const handleCodeSort = (e, property) => {
    const isAsc = codeOrderBy === property && codeOrder === 'asc';
    setCodeOrder(isAsc ? 'desc' : 'asc');
    setCodeOrderBy(property);
    viewCodes({ ...codeFind, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const edit = () => {
    Swal.fire({
      title: t('alert.confirm_edit_title'),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t('element.save'),
      denyButtonText: t('element.cancel'),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        const payload = {
          name: attr.name,
          discount_type: attr.discount_type,
          discount_value: parseFloat(attr.discount_value) || 0,
          max_discount_amount:
            attr.max_discount_amount !== '' ? parseFloat(attr.max_discount_amount) : null,
          min_order_amount:
            attr.is_min_amount === 'Y' ? parseFloat(attr.min_order_amount) || 0 : null,
          is_condition: attr.is_condition,
          is_min_amount: attr.is_min_amount,
          start_date: attr.start_date,
          end_date: attr.end_date,
          enable: attr.enable,
        };

        FetchData('PATCH', `/${mod}/${id}`, app.token, payload).then((result) => {
          setLoading(false);
          if (result.status === 'ok') {
            showNotify(t('alert.editcomplete'), 'success');
            open();
          } else {
            showNotify(t('alert.saveerror'), 'error');
          }
        });
      }
    });
  };

  // ---- Generate Code ----
  const genCodeConfirm = () => {
    if (!Func.verifyLength(genCode.code, 1)) {
      showNotify('กรุณากรอกรหัส/prefix', 'error');
      return;
    }
    if (genCode.mode === 'bulk' && !Func.verifyLength(genCode.amount.toString(), 1)) {
      showNotify('กรุณากรอกจำนวน', 'error');
      return;
    }
    if (genCode.mode === 'single' && !Func.verifyLength(genCode.quota.toString(), 1)) {
      showNotify('กรุณากรอก quota', 'error');
      return;
    }
    setConfirmGen(true);
  };

  const generateCode = () => {
    setLoading(true);
    setModalGen(false);

    const payload = {
      coupon_id: parseInt(id, 10),
      mode: genCode.mode,
      code: genCode.code,
      ...(genCode.mode === 'single' && { is_limit: genCode.is_limit }),
    };
    if (genCode.mode === 'bulk') {
      payload.amount = parseInt(genCode.amount, 10) || 0;
      payload.quota = 1;
    } else {
      payload.quota = parseInt(genCode.quota, 10) || 0;
    }

    FetchData('POST', `/${mod}/generate-code`, app.token, payload).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        showNotify(
          `สร้างรหัสคูปองสำเร็จ ${result.mode === 'bulk' ? `จำนวน ${result.amount} รหัส` : ''}`,
          'success'
        );
        viewCodes({ ...initCodeFind, coupon_id: id });
      } else {
        showNotify(t('alert.saveerror'), 'error');
      }
    });
  };

  // ---- Products ----
  const searchProduct = (data) => {
    setProductFind(data);
    setProductLoading(true);
    FetchData('OPTIONS', `/product/search`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    })
      .then((result) => {
        if (result.status === 'ok') {
          setProductList(result.data ?? []);
          setProductPageTotal(result.total_pages ?? 1);
          setProductPageNo(result.page ?? 1);
        }
        setProductLoading(false);
      })
      .catch(() => {
        setProductLoading(false);
      });
  };

  const handleProductSort = (e, property) => {
    const isAsc = productOrderBy === property && productOrder === 'asc';
    setProductOrder(isAsc ? 'desc' : 'asc');
    setProductOrderBy(property);
    searchProduct({ ...productFind, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const addProductToCoupon = (product) => {
    setProductLoading(true);
    FetchData('POST', `/${mod}/add-product`, app.token, {
      coupon_id: id,
      product_ids: [product.id],
    }).then((result) => {
      setProductLoading(false);
      if (result.status === 'ok') {
        showNotify(`เพิ่มสินค้าสำเร็จ`, 'success');
        open();
      } else {
        showNotify(t('alert.saveerror'), 'error');
      }
    });
  };

  const removeProduct = (barcodeId) => {
    Swal.fire({
      title: t('alert.confirm_delete_title'),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t('element.deleteconfirm'),
      denyButtonText: t('element.cancel'),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        FetchData('POST', `/${mod}/remove-product`, app.token, {
          coupon_id: id,
          product_ids: barcodeId,
        }).then((result) => {
          setLoading(false);
          if (result.status === 'ok') {
            showNotify(t('alert.deletecomplete'), 'success');
            open();
          } else {
            showNotify(t('alert.deleteerror'), 'error');
          }
        });
      }
    });
  };

  const addBrand = (item) => {
    setLoading(true);
    FetchData('POST', `/${mod}/add-product-brand`, app.token, {
      coupon_id: id,
      brand_id: item.id,
    }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        showNotify(`เพิ่มสินค้าตามยี่ห้อสำเร็จ`, 'success');
        open();
      } else {
        showNotify(t('alert.saveerror'), 'error');
      }
    });
  };

  // ---- Render Info Tab (updated layout to match Campaign UI) ----
  const renderInfoTab = () => (
    <Grid container spacing={2}>
      {/* Left: Edit form */}
      <Grid item xs={12} md={3}>
        <MDBox className='card' p={2} mb={2}>
          <MDTypography variant='h6' mb={2}>
            แก้ไขคูปอง
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDInput
                label='ชื่อคูปอง *'
                fullWidth
                variant='standard'
                value={attr.name}
                onChange={(e) => setAttr({ ...attr, name: e.target.value })}
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                fullWidth
                label='ประเภทส่วนลด *'
                value={attr.discount_type}
                select
                variant='standard'
                onChange={(e) => {
                  const newType = e.target.value;
                  // Save current value for the current type
                  attrDiscountValues.current[attr.discount_type] = attr.discount_value;
                  // Restore saved value for the new type (default to 0 if never set)
                  const restored = attrDiscountValues.current[newType];
                  setAttr({
                    ...attr,
                    discount_type: newType,
                    discount_value: restored !== '' && restored !== undefined ? restored : 0,
                  });
                }}
              >
                <MenuItem value='fixed'>คงที่ (฿)</MenuItem>
                <MenuItem value='percentage'>เปอร์เซ็นต์ (%)</MenuItem>
              </MDInput>
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label={`มูลค่าส่วนลด * ${attr.discount_type === 'percentage' ? '(%)' : '(฿)'}`}
                fullWidth
                variant='standard'
                value={attr.discount_value}
                onChange={(e) => {
                  attrDiscountValues.current[attr.discount_type] = e.target.value;
                  setAttr({ ...attr, discount_value: e.target.value });
                }}
                inputProps={{ autoComplete: 'off', type: 'number', min: 0 }}
              />
            </Grid>
            {attr.discount_type === 'percentage' && (
              <Grid item xs={12}>
                <MDInput
                  label='ส่วนลดสูงสุด (฿)'
                  fullWidth
                  variant='standard'
                  value={attr.max_discount_amount}
                  onChange={(e) => setAttr({ ...attr, max_discount_amount: e.target.value })}
                  inputProps={{ autoComplete: 'off', type: 'number', min: 0 }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <MDCheckbox
                label='มีเงื่อนไขยอดขั้นต่ำ'
                checked={attr.is_min_amount === 'Y'}
                onChange={(e) => setAttr({ ...attr, is_min_amount: e.target.checked ? 'Y' : 'N' })}
              />
            </Grid>
            {attr.is_min_amount === 'Y' && (
              <Grid item xs={12}>
                <MDInput
                  label='ยอดสั่งซื้อขั้นต่ำ (฿) *'
                  fullWidth
                  variant='standard'
                  value={attr.min_order_amount}
                  onChange={(e) => setAttr({ ...attr, min_order_amount: e.target.value })}
                  inputProps={{ autoComplete: 'off', type: 'number', min: 0 }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <MDCheckbox
                label='ใช้ได้เฉพาะสินค้าที่กำหนด'
                checked={attr.is_condition === 'Y'}
                onChange={(e) => setAttr({ ...attr, is_condition: e.target.checked ? 'Y' : 'N' })}
              />
            </Grid>
            <Grid item xs={12}>
              <MDDatePicker
                onChange={([date]) => setAttr({ ...attr, start_date: Func.dateFormat(date) })}
                value={new Date(attr.start_date)}
                input={{ label: 'วันที่เริ่มต้น *', variant: 'standard', fullWidth: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDDatePicker
                onChange={([date]) => setAttr({ ...attr, end_date: Func.dateFormat(date) })}
                value={new Date(attr.end_date)}
                input={{ label: 'วันที่สิ้นสุด *', variant: 'standard', fullWidth: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDCheckbox
                label={t('attr.enable')}
                checked={attr.enable === 'Y'}
                onChange={(e) => setAttr({ ...attr, enable: e.target.checked ? 'Y' : 'N' })}
              />
            </Grid>
            <Grid item xs={12} display='flex' justifyContent='flex-end' mt={1}>
              <MDButton variant='outlined' color='success' onClick={edit}>
                <Icon>save</Icon>&nbsp;{t('element.save')}
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </Grid>

      {/* Right: Product list (main) */}
      <Grid item xs={12} md={9}>
        <MDBox className='card' p={2}>
          <MDTypography variant='h6' mb={2}>
            รายการสินค้า
          </MDTypography>
          <CouponProductList
            products={attr.products ?? []}
            productSearch={productSearch}
            setProductSearch={setProductSearch}
            productPage={productPage}
            setProductPage={setProductPage}
            productPerPage={productPerPage}
            setProductPerPage={setProductPerPage}
            productPageShowMenu={productPageShowMenu}
            setProductPageShowMenu={setProductPageShowMenu}
            onAddClick={() => {
              setProductFind(initProductFind);
              setProductList([]);
              setModalProduct(true);
              setTimeout(() => {
                if (txtProductSearch.current) txtProductSearch.current.focus();
              }, 400);
            }}
            onRemove={removeProduct}
            brandButton={<CouponBrand add={addBrand} />}
          />
        </MDBox>
      </Grid>
    </Grid>
  );

  // ---- Render Codes Tab ----
  const renderCodesTab = () => (
    <Grid container spacing={2}>
      {/* Search + Add button row */}
      <Grid item xs={12} sm={9} md={10}>
        <MDBox
          noValidate
          component='form'
          role='form'
          onSubmit={(e) => {
            e.preventDefault();
            viewCodes({ ...codeFind, page: 1 });
          }}
        >
          <MDInput
            name='codesearchform'
            fullWidth
            value={codeFind.search}
            type='search'
            onChange={(e) => setCodeFind({ ...codeFind, search: e.target.value, page: 1 })}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
              ),
              autoComplete: 'off',
            }}
          />
        </MDBox>
      </Grid>
      <Grid item xs={12} sm={3} md={2} display='flex' alignItems='center' justifyContent='flex-end'>
        <MDButton
          variant='outlined'
          color='dark'
          fullWidth
          onClick={() => {
            setGenCode(initGenCode);
            setConfirmGen(false);
            setModalGen(true);
          }}
        >
          <Icon>qr_code</Icon>&nbsp;สร้างรหัส
        </MDButton>
      </Grid>

      {/* Cards */}
      {codeLoading ? (
        <Grid item xs={12} display='flex' justifyContent='center' py={4}>
          <CircularProgress size={32} />
        </Grid>
      ) : codeList.length === 0 ? (
        <Grid item xs={12}>
          <MDBox display='flex' justifyContent='center' alignItems='center' py={6}>
            <MDTypography variant='body2' color='secondary'>
              ยังไม่มีรหัสคูปอง
            </MDTypography>
          </MDBox>
        </Grid>
      ) : (
        codeList.map((c, idx) => {
          const isUsedUp = Number(c.quota) === 0;

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={c.id ?? idx}>
              <MDBox
                sx={{
                  border: '1.5px solid',
                  borderColor: isUsedUp ? 'grey.400' : 'grey.300',
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isUsedUp ? 'grey.100' : 'transparent',
                  opacity: isUsedUp ? 0.6 : 1,
                  position: 'relative',
                }}
              >
                <MDTypography
                  variant='h6'
                  fontWeight='bold'
                  sx={{
                    letterSpacing: 2,
                    wordBreak: 'break-all',
                    textAlign: 'center',
                    color: isUsedUp ? 'grey.500' : 'inherit',
                  }}
                >
                  {c.code}
                </MDTypography>
              </MDBox>
            </Grid>
          );
        })
      )}

      {/* Pagination row */}
      <Grid item xs={12}>
        <Card sx={{ p: 2 }}>
          <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
            <MDButton
              variant={codeShowMenu ? 'contained' : 'outlined'}
              color='dark'
              onClick={(e) => setCodeShowMenu(e.currentTarget)}
            >
              {t('element.show_record', { num: codeFind.perPage })}&nbsp;
              <Icon>keyboard_arrow_down</Icon>
            </MDButton>
            <Menu
              anchorEl={codeShowMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              open={Boolean(codeShowMenu)}
              onClose={() => setCodeShowMenu(null)}
              keepMounted
            >
              {[10, 20, 50, 100].map((num) => (
                <MenuItem
                  key={num}
                  onClick={() => {
                    setCodeShowMenu(null);
                    viewCodes({ ...codeFind, page: 1, perPage: num });
                  }}
                >
                  {t('element.show_record', { num })}
                </MenuItem>
              ))}
            </Menu>
            <Pagination
              count={codePageTotal}
              variant='outlined'
              color='secondary'
              showFirstButton
              showLastButton
              page={codePageNo}
              onChange={(e, p) => viewCodes({ ...codeFind, page: p })}
            />
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );

  // ---- Modal: Generate Code ----
  const renderGenCodeModal = () => (
    <Dialog
      open={modalGen}
      maxWidth='xs'
      keepMounted
      aria-describedby='dialog-gen-code'
      fullWidth
      onClose={() => setModalGen(false)}
    >
      <DialogTitle>
        สร้างรหัสคูปอง
        <IconButton
          aria-label='close'
          onClick={() => setModalGen(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <MDInput
              fullWidth
              label='Mode'
              value={genCode.mode}
              select
              variant='standard'
              onChange={(e) => setGenCode({ ...genCode, mode: e.target.value })}
            >
              <MenuItem value='single'>Single — รหัสเดียว กำหนด quota</MenuItem>
              <MenuItem value='bulk'>Bulk — หลายรหัส (quota = 1 ต่อรหัส)</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label={genCode.mode === 'bulk' ? 'Prefix (≤10 ตัวอักษร)' : 'รหัสคูปอง (≤10 ตัวอักษร)'}
              fullWidth
              variant='standard'
              value={genCode.code}
              onChange={(e) => setGenCode({ ...genCode, code: e.target.value.toUpperCase() })}
              inputProps={{ autoComplete: 'off', maxLength: 10 }}
            />
          </Grid>
          {genCode.mode === 'bulk' ? (
            <Grid item xs={12}>
              <MDInput
                label='จำนวนรหัสที่ต้องการ *'
                fullWidth
                variant='standard'
                value={genCode.amount}
                onChange={(e) => setGenCode({ ...genCode, amount: e.target.value })}
                inputProps={{ autoComplete: 'off', type: 'number', min: 1 }}
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <MDInput
                label='Quota (จำนวนครั้งที่ใช้ได้) *'
                fullWidth
                variant='standard'
                value={genCode.quota}
                onChange={(e) => setGenCode({ ...genCode, quota: e.target.value })}
                inputProps={{ autoComplete: 'off', type: 'number', min: 1 }}
              />
            </Grid>
          )}
          {genCode.mode === 'single' && (
            <Grid item xs={12}>
              <MDCheckbox
                label='จำกัดจำนวนการใช้งาน'
                checked={genCode.is_limit === 1}
                onChange={(e) => setGenCode({ ...genCode, is_limit: e.target.checked ? 1 : 0 })}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        {confirmGen ? (
          <Grid container>
            <Grid item xs={6}>
              <MDButton variant='text' color='error' onClick={() => setConfirmGen(false)}>
                <Icon>cancel</Icon>&nbsp;{t('element.cancel')}
              </MDButton>
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              <MDButton variant='outlined' color='success' onClick={generateCode}>
                <Icon>save</Icon>&nbsp;{t('element.saveconfirm')}
              </MDButton>
            </Grid>
          </Grid>
        ) : (
          <MDButton variant='text' color='info' onClick={genCodeConfirm}>
            <Icon>qr_code</Icon>&nbsp;สร้าง
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );

  // ---- Modal: Search & Add Product ----
  const productHeader = [
    { id: 'pd.id', label: t('attr.image'), align: 'center' },
    { id: 'pd.barcode', label: 'Barcode', align: 'left' },
    { id: 'pd.name', label: 'สินค้า', align: 'left' },
    { id: 'pd.unit_id', label: 'หน่วยนับ', align: 'left', width: 100 },
    { id: 'add', label: '', align: 'center', sort: false, width: 60 },
  ];

  const renderProductModal = () => (
    <Dialog
      open={modalProduct}
      maxWidth='xl'
      keepMounted
      aria-describedby='dialog-add-product'
      fullWidth
      onClose={() => setModalProduct(false)}
    >
      <DialogTitle>
        เพิ่มสินค้าในคูปอง
        <IconButton
          aria-label='close'
          onClick={() => setModalProduct(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <MDBox>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDBox
                noValidate
                component='form'
                role='form'
                onSubmit={(e) => {
                  e.preventDefault();
                  searchProduct({ ...productFind, page: 1 });
                }}
              >
                <MDInput
                  name='product-searchform'
                  fullWidth
                  value={productFind.search}
                  type='search'
                  onChange={(e) =>
                    setProductFind({ ...productFind, search: e.target.value, page: 1 })
                  }
                  InputProps={{
                    inputRef: txtProductSearch,
                    startAdornment: (
                      <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                    ),
                    autoComplete: 'off',
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12}>
              <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
                <Table aria-label='product-search-table'>
                  <TableHeader
                    order={productOrder}
                    orderBy={productOrderBy}
                    onRequestSort={handleProductSort}
                    data={productHeader}
                  />
                  <TableBody>
                    {productLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} align='center'>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : productList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align='center'>
                          <MDTypography variant='caption' color='secondary'>
                            ค้นหาสินค้า
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      productList.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell width={40}>{Func.imageDisplay(row)}</TableCell>
                          <TableCell>{row.barcode}</TableCell>
                          <TableCell>
                            <MDBox>{row.code}</MDBox>
                            <MDBox>{row.name}</MDBox>
                          </TableCell>
                          <TableCell align='left'>{row.unit_name}</TableCell>
                          <TableCell align='center'>
                            <IconButton color='error' onClick={() => addProductToCoupon(row)}>
                              <Icon>add_circle</Icon>
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton
                  variant={productShowMenu ? 'contained' : 'outlined'}
                  color='dark'
                  onClick={(e) => setProductShowMenu(e.currentTarget)}
                >
                  {t('element.show_record', { num: productFind.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                <Menu
                  anchorEl={productShowMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  open={Boolean(productShowMenu)}
                  onClose={() => setProductShowMenu(null)}
                  keepMounted
                >
                  {[7, 10, 20, 100].map((num) => (
                    <MenuItem
                      key={num}
                      onClick={() => {
                        setProductShowMenu(null);
                        searchProduct({ ...productFind, page: 1, perPage: num });
                      }}
                    >
                      {t('element.show_record', { num })}
                    </MenuItem>
                  ))}
                </Menu>
                <Pagination
                  count={productPageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={productPageNo}
                  onChange={(e, p) => searchProduct({ ...productFind, page: p })}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </DialogContent>
    </Dialog>
  );

  const renderNavigate = () => (
    <Card sx={{ p: 2 }}>
      <Grid container alignItems='center' spacing={1}>
        <Grid item>
          {onBack ? (
            <MDButton variant='contained' color='dark' onClick={onBack}>
              <Icon>arrow_back</Icon>&nbsp;{t('element.back')}
            </MDButton>
          ) : (
            <NavLink to='/coupon_list'>
              <MDButton variant='contained' color='dark'>
                <Icon>arrow_back</Icon>&nbsp;{t('element.back')}
              </MDButton>
            </NavLink>
          )}
        </Grid>
        <Grid item>
          <MDTypography variant='h6' color='text'>
            #{attr.id} — {attr.name}
          </MDTypography>
        </Grid>
      </Grid>
    </Card>
  );

  const renderOpen = () => (
    <MDBox mb={7}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {renderNavigate()}
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <MDBox sx={{ width: '100%' }}>
              <MDBox borderBottom={1} borderColor='divider'>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label='coupon-detail-tabs'>
                  <Tab label={t('element.info')} />
                  <Tab label='รหัสคูปอง' onClick={() => viewCodes({ ...initCodeFind })} />
                </Tabs>
              </MDBox>
              <TabPanel value={tab} index={0}>
                {renderInfoTab()}
              </TabPanel>
              <TabPanel value={tab} index={1}>
                {renderCodesTab()}
              </TabPanel>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    if (id !== undefined) open();
    else view(find);
  }, [id]);

  const renderPage = () => {
    if (id === undefined) return renderList();
    if (onBack) {
      return (
        <DashboardLayout>
          <Navbar route={[t('menu.pos'), 'คูปอง', `#${id}`]} />
          {renderOpen()}
          {renderGenCodeModal()}
          {renderProductModal()}
          <Footer />
          {renderLoading()}
          {notify}
        </DashboardLayout>
      );
    }
    return (
      <DashboardLayout>
        <Navbar route={[t('menu.pos'), 'คูปอง', `#${id}`]} />
        {renderOpen()}
        {renderGenCodeModal()}
        {renderProductModal()}
        <Footer />
        {renderLoading()}
        {notify}
      </DashboardLayout>
    );
  };

  return renderPage();
}

CouponDetail.defaultProps = {
  id: undefined,
  onBack: null,
};

CouponDetail.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBack: PropTypes.func,
};

export default CouponDetail;
